import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { ArrowRightSmall, Xmark } from '@peacecover/icon';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { formatDateTime } from '@pocapan/shared/utils/DateUtil';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import RefreshOutline from '../../../icons/RefreshOutline';
import type { components } from '@pocapan/shared/types/schema';

type ChatRoomMessage = components['schemas']['ChatRoomMessage'];
type AudioChatRoomMessage = ChatRoomMessage & {
  media_url?: string | null;
  medialUrl?: string | null;
  audioUrl?: string | null;
  audio_url?: string | null;
};

interface MessageBubbleProps {
  message: ChatRoomMessage;
  previousDate?: string | null;
  nextDate?: string | null;
  isFirstMessage?: boolean;
  onProfileImageClick?: () => void;
  isLastInGroup?: boolean;
  isFirstInGroup?: boolean;
  isChatRoomLastMessage?: boolean;
  isPopupOpen?: () => void;
}

const MessageBubble = ({
  message,
  previousDate = null,
  nextDate = null,
  isFirstMessage = false,
  onProfileImageClick,
  isLastInGroup = true,
  isFirstInGroup = true,
  isChatRoomLastMessage = false,
  isPopupOpen
}: MessageBubbleProps) => {
  // typing 가상 메시지는 messageType: 'notice' + content: '__typing__'
  const isTyping = message.messageType === 'notice' && message.content === '__typing__';
  // @ts-expect-error - 'link' messageType not yet in schema
  const isLinkMessage = message.messageType === 'link';
  const audioMediaUrl = getAudioMediaUrl(message);
  const isSequentialTyping =
    isTyping && typeof message.chatRoomMessageId === 'string'
      ? message.chatRoomMessageId.startsWith('typing-seq-')
      : false;
  const isMine = isTyping ? false : Boolean(message.isMine);
  const timeText = message.sentAt ? formatDateTime(message.sentAt) : '';

  const { colorMode } = useColorMode();

  const isDateChanged =
    !isTyping &&
    (isFirstMessage ||
      (previousDate && formatDateTime(previousDate, 'P') !== formatDateTime(message.sentAt, 'P')));
  // 다음 메시지의 날짜가 다르면 현재 메시지는 이전 날짜의 마지막 메시지
  const isDateChangedAfter =
    !isTyping && nextDate && formatDateTime(message.sentAt, 'P') !== formatDateTime(nextDate, 'P');
  const showDateHeader = isDateChanged;

  return (
    <>
      {showDateHeader && (
        <div className="flex justify-center mb-[12px]">
          <div className="py-[8px]">
            <span className="text-[12px] font-medium text-text-main-lv4">
              {formatDateTime(message.sentAt, 'PPP')}
            </span>
          </div>
        </div>
      )}

      <div
        className={`flex ${isMine ? 'justify-end' : 'justify-start'} ${isLastInGroup ? 'mb-[16px]' : ''} w-full`}
      >
        <div className={`flex ${isMine ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2`}>
          {!isMine && (isLastInGroup || isDateChangedAfter) && (
            <button
              type="button"
              className="w-[26px] h-[26px] rounded-full flex items-center justify-center flex-shrink-0"
              onClick={onProfileImageClick}
            >
              <ProfileAvatar
                thumbnailUrl={message.userProfilePictureUrl}
                widthAndHeight="26px"
                svgWidthAndHeight="24px"
              />
            </button>
          )}
          {!isMine && !isLastInGroup && !isDateChangedAfter && (
            <div className="w-[26px] h-[26px] flex-shrink-0" />
          )}

          <div>
            {message.displayName &&
              (isFirstInGroup || isDateChanged) &&
              (!isTyping || !isSequentialTyping) && (
                <div
                  className={`text-[11px] text-text-main-lv4 mb-[4px] ml-[6px] ${isMine ? 'text-right' : 'text-left'}`}
                >
                  {isMine ? t('BettieChatRoom.me') : message.displayName}
                </div>
              )}
            <div
              className={`flex ${isMine ? 'flex-row-reverse' : 'flex-row'} items-end w-full gap-[4px]`}
            >
              <div
                className={`rounded-[16px] mb-[5px] max-w-[250px] ${isLinkMessage ? 'py-[16px]' : 'px-[16px] py-[14px]'}  ${isMine ? 'bg-primary-light text-background-light' : 'bg-surface-lv2 text-text-main-lv2'}`}
              >
                {isTyping ? (
                  <div className="typing-indicator mx-auto">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : isLinkMessage ? (
                  <LinkMessage content={message.content} />
                ) : audioMediaUrl ? (
                  <AudioMessage content={message.content} mediaUrl={audioMediaUrl} />
                ) : (
                  <p className="text-[15px] font-medium leading-[18px] whitespace-pre-line break-words">
                    {message.content}
                  </p>
                )}
              </div>
              {/* 내 메시지에 대해서만 실패 메시지 팝업 표시 */}
              {!message.seenAt && isMine ? (
                <button
                  type="button"
                  className="flex items-center gap-[7px] px-[8px] py-[4px] rounded-[10px] bg-surface-lv2"
                  onClick={() => isPopupOpen?.()}
                >
                  <RefreshOutline size={14} fill={colorMode === 'dark' ? '#FAFAFA' : '#222222'} />
                  <Xmark size={14} className="text-on-warning" />
                </button>
              ) : (
                (() => {
                  // - 채팅방 마지막 메시지가 내 메시지 + seenAt일 때: "답장없이 보냄" + timeText
                  // - 내 메시지 그룹 중 !isLastInGroup일 경우: "답장없이 보냄"
                  // - 이 외의 경우: timeText 표시
                  const showNoReply =
                    (isMine && !!message.seenAt && isChatRoomLastMessage) ||
                    (isMine && !isLastInGroup);
                  const showTime = isMine
                    ? (isMine && !!message.seenAt && isChatRoomLastMessage) || !showNoReply
                    : isLastInGroup || isDateChangedAfter;

                  if (!showNoReply && !showTime) return null;

                  return (
                    <div
                      className={`text-[10px] text-text-main-lv4 ${isMine ? 'text-right' : 'text-left'}`}
                    >
                      {showNoReply && <p>{t('BettieChatRoom.noReply')}</p>}
                      {showTime && timeText}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageBubble;

const getAudioMediaUrl = (message: ChatRoomMessage): string | undefined => {
  if (message.messageType !== 'audio') return undefined;

  const audioMessage = message as AudioChatRoomMessage;
  const mediaUrl =
    audioMessage.mediaUrl ??
    audioMessage.media_url ??
    audioMessage.medialUrl ??
    audioMessage.audioUrl ??
    audioMessage.audio_url;

  return typeof mediaUrl === 'string' && mediaUrl.length > 0 ? mediaUrl : undefined;
};

const AudioMessage = ({ content, mediaUrl }: { content: string; mediaUrl: string }) => {
  return (
    <div className="flex w-[218px] max-w-full flex-col gap-[10px]">
      {content && (
        <p className="text-[15px] font-medium leading-[18px] whitespace-pre-line break-words">
          {content}
        </p>
      )}
      <audio
        data-no-swipe="true"
        className="w-full"
        controls
        controlsList="nodownload"
        src={mediaUrl}
        onContextMenu={(event) => event.preventDefault()}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
};

const LinkMessage = ({ content }: { content: string }) => {
  const parsedContent = JSON.parse(content);

  const linkUrl = parsedContent.url;
  const displayLinkUrl = linkUrl.replace(
    /^(https?:\/\/)?(dev|app)\.pocapan\.com(?=\/|$)/i,
    (_match: string, protocol?: string) => `${protocol ?? ''}fanhearts.com`
  );

  return (
    <button type="button" onClick={() => openNewTab(linkUrl)}>
      <div className="flex items-center justify-between gap-[8px] px-[16px] mb-[12px]">
        <div className="flex items-center gap-[8px]">
          <ProfileAvatar
            thumbnailUrl={parsedContent.authorProfileImage}
            widthAndHeight="19px"
            svgWidthAndHeight="19px"
          />
          <span className="text-[13px] font-medium leading-[16px] text-text-main-lv2">
            {parsedContent.authorName}
          </span>
        </div>
        <ArrowRightSmall size={14} className="text-text-main-lv4" />
      </div>
      <img
        src={parsedContent.image}
        alt={parsedContent.body}
        className="w-[237px] h-[93px] object-cover"
      />
      <div className="flex flex-col items-start gap-[5px] px-[16px] mt-[12px]">
        <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2 max-w-[205px] truncate line-clamp-1">
          {parsedContent.text}
        </p>
        <p className="text-[11px] font-medium leading-[13px] text-on-varified text-left max-w-[205px] truncate line-clamp-1">
          {displayLinkUrl}
        </p>
      </div>
    </button>
  );
};
